import { unitsForAge } from '.';
import { SupportedLanguage } from '../context/animalHeaven.types';

export interface Action {
  payload: Object;
  type: string;
}

export const createUser = ({
  captchaToken,
  email,
  firstName,
  language,
  lastName,
  password,
  phone,
  phoneCode,
}: {
  captchaToken: string;
  email: string;
  firstName: string;
  language: SupportedLanguage;
  lastName: string;
  password: string;
  phone: string;
  phoneCode: string;
}): Action => ({
  payload: {
    captchaToken,
    email,
    firstName,
    language,
    lastName,
    password,
    phone,
    phoneCode,
  },
  type: 'CREATE_USER',
});

export const login = ({ email, password }: { email: string; password: string }): Action => ({
  payload: {
    email,
    password,
  },
  type: 'LOGIN',
});

export const sendCodeToRecoverPassword = ({ email }: { email: string }): Action => ({
  payload: {
    email,
  },
  type: 'SEND_CODE_TO_RECOVER_PASSWORD',
});

export const recoverPassword = ({
  code,
  email,
  password,
}: {
  code: string;
  email: string;
  password: string;
}): Action => ({
  payload: {
    code,
    email,
    password,
  },
  type: 'RECOVER_PASSWORD',
});

export const confirmAccount = ({ id }: { id: string }): Action => ({
  type: 'CONFIRM_ACCOUNT',
  payload: { id },
});

export const getInitialState = (): Action => ({
  type: 'GET_INITIAL_STATE',
  payload: {},
});

export const logout = ({ token }: { token: string }): Action => ({
  type: 'LOGOUT',
  payload: { token },
});

export const updateUser = ({
  firstName,
  language,
  lastName,
  phone,
  phoneCode,
}: {
  firstName: string;
  language: SupportedLanguage;
  lastName: string;
  phone: string;
  phoneCode: string;
}): Action => ({
  payload: {
    firstName,
    language,
    lastName,
    phone,
    phoneCode,
  },
  type: 'UPDATE_USER',
});

export const getPetTypes = (): Action => ({
  type: 'GET_PET_TYPES',
  payload: {},
});

export const createMyAdoptionCenter = ({
  address,
  city,
  contactEmail,
  contactPhone,
  country,
  description,
  facebook,
  files,
  instagram,
  link,
  name,
  startDateOfOperation,
  youtube,
}: {
  name: string;
  country: string;
  city: string;
  address: string;
  files: File[];
  contactEmail: string;
  contactPhone: string;
  description: string;
  link: string;
  facebook: string;
  instagram: string;
  startDateOfOperation: string;
  youtube: string;
}): FormData => {
  const data = new FormData();
  data.append('name', name);
  data.append('startDateOfOperation', startDateOfOperation);
  data.append('country', country);
  data.append('address', address);
  data.append('contactEmail', contactEmail);
  data.append('contactPhone', contactPhone);
  data.append('description', description);
  data.append('link', link);
  data.append('facebook', facebook);
  data.append('instagram', instagram);
  data.append('city', city);
  data.append('youtube', youtube);
  data.append('type', 'ADD_ADOPTION_CENTER');
  files.forEach((file) => data.append('files', file));
  return data;
};

export const editAdoptionCenter = ({
  address,
  city,
  contactEmail,
  contactPhone,
  country,
  description,
  facebook,
  files,
  filesOrder,
  id,
  instagram,
  link,
  name,
  startDateOfOperation,
  youtube,
}: {
  name: string;
  country: string;
  city: string;
  address: string;
  files: File[];
  contactEmail: string;
  contactPhone: string;
  description: string;
  link: string;
  facebook: string;
  id?: string;
  instagram: string;
  startDateOfOperation: string;
  youtube: string;
  filesOrder: (string | null)[];
}): FormData => {
  const data = new FormData();
  data.append('name', name);
  data.append('startDateOfOperation', startDateOfOperation);
  data.append('country', country);
  data.append('address', address);
  data.append('contactEmail', contactEmail);
  data.append('contactPhone', contactPhone);
  data.append('description', description);
  data.append('link', link);
  data.append('facebook', facebook);
  data.append('instagram', instagram);
  data.append('city', city);
  data.append('youtube', youtube);
  data.append('id', id || '');
  data.append('type', 'EDIT_ADOPTION_CENTER');
  files.forEach((file) => data.append('files', file));
  filesOrder.forEach((url) => data.append('filesOrder', String(url)));
  return data;
};

export const deleteAdoptionCenter = (id?: string): Action => ({
  type: 'DELETE_ADOPTION_CENTER',
  payload: { id },
});

export const addMemorial = ({
  additionalPetInformation,
  cityWherePetLived,
  countryWherePetLived,
  dateOfDeath,
  files,
  livedHappilyWith,
  petDescription,
  petName,
  petType,
  stripeProductId,
}: {
  additionalPetInformation: string;
  cityWherePetLived: string | undefined;
  countryWherePetLived: string;
  dateOfDeath: string;
  files: File[];
  livedHappilyWith: string;
  petDescription: string;
  petName: string;
  petType: number;
  stripeProductId: string;
}): FormData => {
  const data = new FormData();
  data.append('additionalPetInformation', additionalPetInformation);
  data.append('cityWherePetLived', cityWherePetLived || '');
  data.append('countryWherePetLived', countryWherePetLived);
  data.append('dateOfDeath', dateOfDeath);
  data.append('livedHappilyWith', livedHappilyWith);
  data.append('petDescription', petDescription);
  data.append('petName', petName);
  data.append('petType', String(petType));
  data.append('stripeProductId', stripeProductId);
  data.append('type', 'ADD_MEMORIAL');
  files.forEach((file, i) => data.append('files', file));

  return data;
};

export const giveLikeAdoptionCenter = ({ id }: { id: string }): Action => ({
  type: 'GIVE_LIKE_ADOPTION_CENTER',
  payload: { id },
});

export const deleteMemorial = ({ memorialId }: { memorialId: string }): Action => ({
  type: 'DELETE_MEMORIAL',
  payload: { memorialId },
});

export const getMemorial = ({ memorialId }: { memorialId: string }): Action => ({
  type: 'GET_MEMORIAL',
  payload: { memorialId },
});

export const editMemorial = ({
  additionalPetInformation,
  cityWherePetLived,
  countryWherePetLived,
  dateOfDeath,
  files,
  filesOrder,
  livedHappilyWith,
  memorialId,
  petDescription,
  petName,
  petType,
}: {
  additionalPetInformation: string;
  cityWherePetLived: string | undefined;
  countryWherePetLived: string;
  dateOfDeath: string;
  files: File[];
  filesOrder: (string | null)[];
  livedHappilyWith: string;
  memorialId: string;
  petDescription: string;
  petName: string;
  petType: number;
}): FormData => {
  const data = new FormData();
  data.append('additionalPetInformation', additionalPetInformation);
  data.append('cityWherePetLived', cityWherePetLived || '');
  data.append('countryWherePetLived', countryWherePetLived);
  data.append('dateOfDeath', dateOfDeath);
  data.append('livedHappilyWith', livedHappilyWith);
  data.append('memorialId', memorialId);
  data.append('petDescription', petDescription);
  data.append('petName', petName);
  data.append('petType', String(petType));
  data.append('type', 'EDIT_MEMORIAL');
  files.forEach((file) => data.append('files', file));
  filesOrder.forEach((url) => data.append('filesOrder', String(url)));

  return data;
};

export const changePassword = ({
  newPassword,
  oldPassword,
}: {
  newPassword: string;
  oldPassword: string;
}): Action => ({ type: 'CHANGE_PASSWORD', payload: { newPassword, oldPassword } });

export const getMemorials = ({
  cityWherePetLived = null,
  countryWherePetLived = null,
  from,
  limit,
  petName,
  petType,
  sortBy,
}: {
  cityWherePetLived?: string | null;
  countryWherePetLived?: string | null;
  from: number;
  limit: number;
  petName?: string;
  petType?: number | null;
  sortBy: 'createdAt' | 'likes';
}): Action => ({
  type: 'GET_MEMORIALS',
  payload: {
    cityWherePetLived,
    countryWherePetLived,
    from,
    limit,
    petName: petName || '',
    petType: petType ?? null,
    sortBy,
  },
});

export const getAdoptionCenters = ({
  city,
  country,
  from,
  limit,
  id,
  sortBy,
}: {
  city?: string;
  country?: string;
  from: number;
  limit: number;
  id?: string;
  sortBy: 'createdAt' | 'likes';
}): Action => ({
  type: 'GET_ADOPTION_CENTERS',
  payload: {
    city: city || null,
    country: country || null,
    from,
    limit,
    id: id || null,
    sortBy,
  },
});

export const giveLike = ({ memorialId }: { memorialId: string }): Action => ({
  type: 'GIVE_LIKE',
  payload: { memorialId },
});

export const giveFood = ({ memorialId }: { memorialId: string }): Action => ({
  type: 'GIVE_FOOD',
  payload: { memorialId },
});

export const giveToyOrPlace = ({ memorialId }: { memorialId: string }): Action => ({
  type: 'GIVE_TOY_OR_PLACE',
  payload: { memorialId },
});

export const getStripeProducts = () => ({
  type: 'GET_STRIPE_PRODUCTS',
  payload: {},
});

export const getMyMemorials = ({
  from,
  limit,
  status,
}: {
  from: number;
  limit: number;
  status: string;
}): Action => ({
  type: 'GET_MY_MEMORIALS',
  payload: {
    from,
    limit,
    status,
  },
});

export const getMyAdoptionCenter = (): Action => ({
  type: 'GET_MY_ADOPTION_CENTER',
  payload: {},
});

export const hideMemorial = ({ memorialId }: { memorialId: string }): Action => ({
  type: 'HIDE_MEMORIAL',
  payload: { memorialId },
});

export const showMemorial = ({ memorialId }: { memorialId: string }): Action => ({
  type: 'SHOW_MEMORIAL',
  payload: { memorialId },
});

export const getLinkToPayMemorial = ({ memorialId }: { memorialId: string }): Action => ({
  type: 'GET_LINK_TO_PAY_MEMORIAL',
  payload: { memorialId },
});

export const cancelSubscription = ({ memorialId }: { memorialId: string }): Action => ({
  type: 'CANCEL_SUBSCRIPTION',
  payload: { memorialId },
});

export const renovateSubscription = ({ memorialId }: { memorialId: string }): Action => ({
  type: 'RENOVATE_SUBSCRIPTION',
  payload: { memorialId },
});

export const getAdoptionCenter = ({ id }: { id: string }): Action => ({
  type: 'GET_ADOPTION_CENTER',
  payload: { id },
});

export const getAdoptionCentersForSelect = (): Action => ({
  type: 'GET_ADOPTION_CENTERS_FOR_SELECT',
  payload: {},
});

export const evaluateAdoptionCenter = ({
  adoptionCenterId,
  rating,
}: {
  adoptionCenterId: string;
  rating: number;
}): Action => ({
  type: 'EVALUATE_ADOPTION_CENTER',
  payload: { adoptionCenterId, rating },
});

export const getAnimalSizes = (): Action => ({
  type: 'GET_ANIMAL_SIZES',
  payload: {},
});

export const addAnimalForAdoption = ({
  age,
  animalSize,
  files,
  petCharacteristics,
  petDescription,
  petName,
  petType,
  unitForAge,
}: {
  age: string;
  animalSize: number;
  files: File[];
  petCharacteristics: number[];
  petDescription: string;
  petName: string;
  petType: number;
  unitForAge: (typeof unitsForAge)[number];
}): FormData => {
  const data = new FormData();
  data.append('age', age);
  data.append('animalSize', String(animalSize));
  data.append('petDescription', petDescription);
  data.append('petName', petName);
  data.append('petType', String(petType));
  data.append('unitForAge', unitForAge);
  data.append('type', 'ADD_ANIMAL_FOR_ADOPTION');
  petCharacteristics.forEach((id) => data.append('petCharacteristics', String(id)));
  files.forEach((file) => data.append('files', file));

  return data;
};

export const getAnimalForAdoption = ({ animalId }: { animalId: string }): Action => ({
  type: 'GET_ANIMAL_FOR_ADOPTION',
  payload: { animalId },
});

export const giveLikeInAnimalForAdoption = ({ animalId }: { animalId: string }): Action => ({
  type: 'GIVE_LIKE_IN_ANIMAL_FOR_ADOPTION',
  payload: { animalId },
});

export const getAnimalsForAdoption = ({
  adoptionCenterId = null,
  animalSize,
  city = null,
  country = null,
  from,
  limit,
  petCharacteristic,
  petType,
  sortBy,
}: {
  adoptionCenterId?: string | null;
  animalSize: number | null;
  city?: string | null;
  country?: string | null;
  from: number;
  limit: number;
  petCharacteristic: number | null;
  petType: number | null;
  sortBy: 'createdAt' | 'likes';
}): Action => ({
  type: 'GET_ANIMALS_FOR_ADOPTION',
  payload: {
    adoptionCenterId,
    animalSize,
    city,
    country,
    from,
    limit,
    petCharacteristic,
    petType,
    sortBy,
  },
});

export const getMyAnimalsForAdoption = ({
  from,
  limit,
  petName,
  status,
}: {
  from: number;
  limit: number;
  petName: string;
  status: string;
}): Action => ({
  type: 'GET_MY_ANIMALS_FOR_ADOPTION',
  payload: {
    from,
    limit,
    petName,
    status,
  },
});

export const getPetCharacteristics = (): Action => ({
  type: 'GET_PET_CHARACTERISTICS',
  payload: {},
});

export const deleteAnimalForAdoption = ({ id }: { id: string }): Action => ({
  type: 'DELETE_ANIMAL_FOR_ADOPTION',
  payload: { id },
});

export const editAnimalForAdoption = ({
  age,
  animalSize,
  files,
  filesOrder,
  id,
  petCharacteristics,
  petDescription,
  petName,
  petType,
  unitForAge,
}: {
  age: string;
  animalSize: number;
  files: File[];
  filesOrder: (string | null)[];
  id: string;
  petCharacteristics: number[];
  petDescription: string;
  petName: string;
  petType: number;
  unitForAge: (typeof unitsForAge)[number];
}): FormData => {
  const data = new FormData();
  data.append('age', age);
  data.append('animalSize', String(animalSize));
  data.append('id', id);
  data.append('petDescription', petDescription);
  data.append('petName', petName);
  data.append('petType', String(petType));
  data.append('unitForAge', unitForAge);
  data.append('type', 'EDIT_ANIMAL_FOR_ADOPTION');
  petCharacteristics.forEach((id) => data.append('petCharacteristics', String(id)));
  files.forEach((file) => data.append('files', file));
  filesOrder.forEach((url) => data.append('filesOrder', String(url)));

  return data;
};

export const hideAnimalForAdoption = ({ id }: { id: string }): Action => ({
  type: 'HIDE_ANIMAL_FOR_ADOPTION',
  payload: { id },
});

export const showAnimalForAdoption = ({ id }: { id: string }): Action => ({
  type: 'SHOW_ANIMAL_FOR_ADOPTION',
  payload: { id },
});

export const requestAdoptionLoggedUser = ({ petId }: { petId: string }): Action => ({
  type: 'REQUEST_ADOPTION_LOGGED_USER',
  payload: { petId },
});

export const requestAdoptionNotLoggedUser = ({
  email,
  firstName,
  lastName,
  petId,
  phone,
  phoneCode,
}: {
  email: string;
  firstName: string;
  lastName: string;
  petId: string;
  phone: string;
  phoneCode: string;
}): Action => ({
  type: 'REQUEST_ADOPTION_NOT_LOGGED_USER',
  payload: { email, firstName, lastName, petId, phone, phoneCode },
});

export const deleteRequestAdoptionLoggedUser = ({
  petId,
  userId,
}: {
  petId: string;
  userId: string;
}): Action => ({
  type: 'DELETE_REQUEST_ADOPTION_LOGGED_USER',
  payload: { petId, userId },
});

export const deleteRequestAdoptionNotLoggedUser = ({
  email,
  petId,
}: {
  email: string;
  petId: string;
}): Action => ({
  type: 'DELETE_REQUEST_ADOPTION_NOT_LOGGED_USER',
  payload: { email, petId },
});

export const addCommentToAdoptionCenterLoggedUser = ({
  adoptionCenterId,
  comment,
}: {
  adoptionCenterId: string;
  comment: string;
}): Action => ({
  type: 'ADD_COMMENT_TO_ADOPTION_CENTER_LOGGED_USER',
  payload: { adoptionCenterId, comment },
});

export const getAdoptionCenterComments = ({
  adoptionCenterId,
  from,
  limit,
}: {
  adoptionCenterId: string;
  from: number;
  limit: number;
}): Action => ({
  type: 'GET_ADOPTION_CENTER_COMMENTS',
  payload: { adoptionCenterId, from, limit },
});

export const addCommentToAdoptionCenterNotLoggedUser = ({
  adoptionCenterId,
  captchaToken,
  comment,
  email,
  firstName,
  lastName,
  phone,
  phoneCode,
}: {
  adoptionCenterId: string;
  captchaToken: string;
  comment: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  phoneCode: string;
}): Action => ({
  type: 'ADD_COMMENT_TO_ADOPTION_CENTER_NOT_LOGGED_USER',
  payload: {
    adoptionCenterId,
    captchaToken,
    comment,
    email,
    firstName,
    lastName,
    phone,
    phoneCode,
  },
});

export const deleteCommentInAdoptionCenter = ({ commentId }: { commentId: string }): Action => ({
  type: 'DELETE_COMMENT_IN_ADOPTION_CENTER',
  payload: { commentId },
});

export const editCommentInAdoptionCenter = ({
  comment,
  commentId,
}: {
  comment: string;
  commentId: string;
}): Action => ({
  type: 'EDIT_COMMENT_IN_ADOPTION_CENTER',
  payload: { comment, commentId },
});

export const changeDateInApi = (): Action => ({
  type: 'CHANGE_DATE_IN_API',
  payload: {},
});

export const deleteAccount = (id?: string): Action => ({
  type: 'DELETE_ACCOUNT',
  payload: { id },
});

export const contactUs = ({
  captchaToken,
  email,
  firstName,
  lastName,
  message,
}: {
  captchaToken: string | null;
  email: string;
  firstName: string;
  lastName: string;
  message: string;
}): Action => ({
  type: 'CONTACT_US',
  payload: { captchaToken, email, firstName, lastName, message },
});

export const adminGetUsers = ({ from, limit }: { from: number; limit: number }): Action => ({
  type: 'ADMIN_GET_USERS',
  payload: { from, limit },
});

export const adminUpdateUser = ({
  email,
  firstName,
  language,
  lastName,
  password,
  phone,
  phoneCode,
  userId,
}: {
  email: string;
  firstName: string;
  language: SupportedLanguage;
  lastName: string;
  password: string | null;
  phone: string;
  phoneCode: string;
  userId: string;
}): Action => ({
  payload: {
    email,
    firstName,
    language,
    lastName,
    password,
    phone,
    phoneCode,
    userId,
  },
  type: 'ADMIN_UPDATE_USER',
});

export const adminGetMemorials = ({ from, limit }: { from: number; limit: number }): Action => ({
  type: 'ADMIN_GET_MEMORIALS',
  payload: { from, limit },
});

export const adminGetAdoptionCenters = ({
  from,
  limit,
}: {
  from: number;
  limit: number;
}): Action => ({
  type: 'ADMIN_GET_ADOPTION_CENTERS',
  payload: { from, limit },
});

export const adminGetAnimalsForAdoption = ({
  adoptionCenterId,
  from,
  limit,
}: {
  adoptionCenterId: string | null;
  from: number;
  limit: number;
}): Action => ({
  type: 'ADMIN_GET_ANIMALS_FOR_ADOPTION',
  payload: { adoptionCenterId, from, limit },
});

export const adminGetComments = ({
  adoptionCenterId,
  from,
  limit,
}: {
  adoptionCenterId: string | null;
  from: number;
  limit: number;
}): Action => ({
  type: 'ADMIN_GET_COMMENTS',
  payload: { adoptionCenterId, from, limit },
});

export const adminEditCommentInAdoptionCenter = ({
  comment,
  email,
  firstName,
  id,
  lastName,
  phone,
  phoneCode,
}: {
  comment: string;
  email: string;
  firstName: string;
  id: string;
  lastName: string;
  phone: string;
  phoneCode: string;
}): Action => ({
  type: 'ADMIN_EDIT_COMMENT_IN_ADOPTION_CENTER',
  payload: {
    comment,
    email,
    firstName,
    id,
    lastName,
    phone,
    phoneCode,
  },
});

export const getLinkToPayMemorialForever = ({ memorialId }: { memorialId: string }): Action => ({
  type: 'GET_LINK_TO_PAY_MEMORIAL_FOREVER',
  payload: { memorialId },
});

export const shareMemorial = ({ id, mustDebugURL }: { id: string; mustDebugURL: boolean }) => ({
  type: 'SHARE_MEMORIAL',
  payload: { id, mustDebugURL },
});

export const shareAnimalForAdoption = ({
  id,
  mustDebugURL,
}: {
  id: string;
  mustDebugURL: boolean;
}) => ({
  type: 'SHARE_ANIMAL_FOR_ADOPTION',
  payload: { id, mustDebugURL },
});

export const shareAdoptionCenter = ({
  id,
  mustDebugURL,
}: {
  id: string;
  mustDebugURL: boolean;
}) => ({
  type: 'SHARE_ADOPTION_CENTER',
  payload: { id, mustDebugURL },
});
